import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { CircleNotch } from '@phosphor-icons/react';

/*
 * Two sizes only. The old 26px outline button is gone — `sm` is 28px, which
 * keeps it on the 4px grid and inside a 44px table row.
 *
 * The boolean variant props are kept so call sites don't churn, but they now
 * resolve to five visual variants. Spec: core-handover/02-components.md.
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded font-semibold whitespace-nowrap transition-colors relative focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

export const SIZE = {
  md: 'h-9 px-4 text-sm', // 36px — default everywhere
  sm: 'h-7 px-3 text-xs', // 28px — table rows and panel headers
};

// legacy size props -> the two that survive
const SIZE_ALIAS = {
  xxs: 'sm',
  xs: 'sm',
  sm: 'sm',
  md: 'md',
  default: 'md',
  lg: 'md',
  xl: 'md',
};

const RING = 'focus-visible:ring-brand-600';

export const VARIANT = {
  primary: cn('bg-brand-600 text-white hover:bg-brand-700', RING),
  secondary: cn('bg-neutral-100 text-neutral-800 hover:bg-neutral-200', RING),
  outline: cn(
    'bg-white border border-neutral-300 text-brand-700 hover:bg-brand-50 hover:border-brand-600',
    RING,
  ),
  danger: cn(
    'bg-danger-600 text-white hover:bg-danger-700',
    'focus-visible:ring-danger-600',
  ),
  ghost: cn('text-neutral-700 hover:bg-neutral-100', RING),
};

const DISABLED = {
  outline: 'bg-white border border-neutral-200 text-neutral-400',
  other: 'bg-neutral-200 text-neutral-400',
};

const ICON_SIZE = { md: 16, sm: 16 };

const Button = React.forwardRef(
  (
    {
      size,
      primary,
      secondary,
      success,
      danger,
      dark,
      link,
      outline,
      transparent,
      defaultShow,
      isLoading,
      loadingLabel,
      className,
      children,
      icon: Icon,
      ...props
    },
    ref,
  ) => {
    const variant = React.useMemo(() => {
      if (danger) return 'danger';
      if (outline) return 'outline';
      if (link || transparent) return 'ghost';
      // Brand green carries the primary action. Status green never appears on
      // a button — see 02-components.md, "Brand green vs status green".
      if (primary || success) return 'primary';
      return 'secondary';
    }, [primary, success, danger, outline, link, transparent]);

    const key = SIZE_ALIAS[size] || 'md';
    const isDisabled = props.disabled || isLoading;

    return (
      <button
        ref={ref}
        className={cn(
          BASE,
          SIZE[key],
          isDisabled
            ? cn(
                variant === 'outline' ? DISABLED.outline : DISABLED.other,
                'cursor-not-allowed',
              )
            : VARIANT[variant],
          className,
        )}
        type="button"
        {...props}
        disabled={isDisabled}
      >
        {isLoading ? (
          <>
            <CircleNotch size={ICON_SIZE[key]} className="animate-spin" />
            {(loadingLabel || children) && (
              <span>{loadingLabel || children}</span>
            )}
          </>
        ) : (
          <>
            {Icon && <Icon size={ICON_SIZE[key]} aria-hidden="true" />}
            {children && <span>{children}</span>}
          </>
        )}
      </button>
    );
  },
);

Button.defaultProps = {
  className: '',
  size: 'default',
  primary: false,
  secondary: false,
  success: false,
  danger: false,
  dark: false,
  link: false,
  outline: false,
  transparent: false,
  defaultShow: false,
  isLoading: false,
  loadingLabel: '',
  icon: null,
  children: null,
  disabled: false,
};

Button.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(['xxs', 'xs', 'sm', 'md', 'default', 'lg', 'xl']),
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  success: PropTypes.bool,
  danger: PropTypes.bool,
  dark: PropTypes.bool,
  link: PropTypes.bool,
  outline: PropTypes.bool,
  transparent: PropTypes.bool,
  defaultShow: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.instanceOf(Object),
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
    PropTypes.number,
  ]),
};

export default Button;
